import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { SecondaryButton } from '../components/SecondaryButton';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import {
  confirmGiftCardCheckout,
  giftCardApiErrorMessage,
  type GiftCardCheckoutConfirm,
} from '../../lib/gift-card-api';
import { clearGiftCardDraft } from '../../lib/gift-card-draft';
import { toast } from '../../lib/toast';

/**
 * Stripe redirects here on success with ?session_id=<CHECKOUT_SESSION_ID>.
 * We confirm the session against the backend (which talks to Stripe
 * + finalises the gift card row) so the buyer sees their code as
 * soon as the page paints. Handles four states:
 *   - no session id in URL  → "thanks, expect the email"
 *   - paid                  → show code + copy button
 *   - still pending         → "we're processing"
 *   - failed                → friendly error + retry
 */
export default function GiftCardSentPage() {
  const [search] = useSearchParams();
  const sessionId = search.get('session_id') ?? '';

  const [confirm, setConfirm] = useState<GiftCardCheckoutConfirm | null>(null);
  const [loading, setLoading] = useState(!!sessionId);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await confirmGiftCardCheckout(sessionId);
        if (!cancelled) {
          setConfirm(res);
          // Wipe the draft so a follow-up purchase starts clean.
          if (res?.payment_status === 'paid') clearGiftCardDraft();
        }
      } catch (err) {
        if (!cancelled) {
          const msg = giftCardApiErrorMessage(
            err,
            "We couldn't confirm your payment. Check your email — we'll send a receipt as soon as Stripe finalises.",
          );
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const recipientName = confirm?.recipient_first_name ?? 'them';
  const recipientEmail = confirm?.recipient_email ?? null;
  const isPaid = confirm?.gift_card_status === 'PAID';
  const isPending =
    !!confirm &&
    !isPaid &&
    (confirm.gift_card_status === 'PENDING_PAYMENT' ||
      confirm.payment_status === 'unpaid');
  const isFailed =
    !!confirm &&
    (confirm.gift_card_status === 'FAILED' ||
      confirm.gift_card_status === 'CANCELLED');

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="flex flex-col items-center text-center w-full max-w-[420px]">
          {loading ? (
            <>
              <div className="w-[60px] h-[60px] mb-6 rounded-full border-4 border-[#8e2a2f]/20 border-t-[#8e2a2f] animate-spin" />
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] mb-2">
                Confirming your payment…
              </h1>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                This usually takes a second or two.
              </p>
            </>
          ) : isFailed ? (
            <>
              <span className="material-icons text-[#8e2a2f] text-[56px] mb-5">
                error_outline
              </span>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] mb-3">
                Payment didn't go through
              </h1>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] mb-6">
                Your card wasn't charged. You can try again or pick a different payment method.
              </p>
              <Link to="/gift-card" className="w-full">
                <SecondaryButton className="w-full">Try again</SecondaryButton>
              </Link>
            </>
          ) : isPending ? (
            <>
              <span className="material-icons text-[#8e2a2f] text-[56px] mb-5">
                hourglass_top
              </span>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] mb-3">
                We're processing your payment
              </h1>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px]">
                Some payment methods take a few minutes. We'll email you and
                the recipient as soon as it lands.
              </p>
            </>
          ) : (
            <>
              <svg
                width="86"
                height="87"
                viewBox="0 0 86 87"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mb-5"
              >
                <rect width="86" height="87" rx="43" fill="#FFFAFA" />
                <g clipPath="url(#clip0_808_3062)">
                  <path
                    d="M43 50.0449C45.6212 50.0449 47.7461 47.8807 47.7461 45.2109C47.7461 42.5412 45.6212 40.377 43 40.377C40.3788 40.377 38.2539 42.5412 38.2539 45.2109C38.2539 47.8807 40.3788 50.0449 43 50.0449Z"
                    fill="#8E2A2F"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_808_3062">
                    <rect
                      width="54"
                      height="55"
                      fill="white"
                      transform="translate(16 16)"
                    />
                  </clipPath>
                </defs>
              </svg>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] mb-3">
                Gift Card Sent!
              </h1>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px]">
                {isPaid && recipientEmail
                  ? `Your Cherry Picked gift card is on the way to ${recipientEmail}.`
                  : 'Your Cherry Picked gift card is on the way.'}{' '}
                We hope it brings a little joy to {recipientName}.
              </p>
              {/* The redemption code is sent straight to the
                  recipient's email — we deliberately don't show it on
                  the buyer's screen. Surfacing it here lets the buyer
                  redeem the code themselves (defeating the gift) and
                  defeats the surprise for the recipient. */}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
